import { parse } from 'papaparse';

export interface DelimitedTable {
  headers: Array<string>;
  rows: Array<Array<string>>;
  /**
   * True when a row carried more values than the header row named, which happens when a value
   * holds an unquoted delimiter. Those values land in columns the header never named, so the
   * reader needs to be told the file is malformed.
   */
  hasRowsWiderThanHeaders: boolean;
}

// Parsing walks the whole document during render, so refuse anything big enough to freeze the
// tab and let it render as plain text instead.
const MAXIMUM_PARSEABLE_LENGTH = 5_000_000;

// Padding squares the table off, so one very wide row allocates far more cells than the character
// limit above implies.
const MAXIMUM_RENDERED_CELLS = 1_000_000;

const LEADING_BLANK_LINES = /^(?:[ \t]*(?:\r\n|\n|\r))*/;

// The delimiters Papa Parse itself considers. A quote only opens a value at one of these
// boundaries, which keeps a stray quote mid-value (an inch mark) from being read as one.
const FIELD_BOUNDARIES = [',', '\t', ';', '|'];

// The header record runs to the first line break outside a quoted value, which is not the same as
// the first line — a quoted column name can hold a line break of its own.
const firstRecordOf = (text: string): string => {
  const start = LEADING_BLANK_LINES.exec(text)?.[0].length ?? 0;
  let inQuotes = false;
  let index = start;

  while (index < text.length) {
    const character = text[index];

    if (character === '"') {
      if (!inQuotes) {
        inQuotes = index === start || FIELD_BOUNDARIES.includes(text[index - 1]);
      } else if (text[index + 1] === '"') {
        // A doubled quote is a literal quote inside the value.
        index += 1;
      } else {
        inQuotes = false;
      }
    } else if (!inQuotes && (character === '\n' || character === '\r')) {
      return text.slice(start, index);
    }

    index += 1;
  }

  return text.slice(start);
};

// Papa Parse guesses the delimiter by looking for a consistent field count across rows, so a file
// with ragged rows falls back to a comma and collapses into one column. The header row is the one
// row that reflects the real delimiter, so guess from it alone and pass the answer on.
const detectDelimiter = (text: string): string | undefined => {
  const { meta, errors } = parse(firstRecordOf(text));
  return errors.some((error) => error.type === 'Delimiter') ? undefined : meta.delimiter;
};

const padToWidth = (row: Array<string>, width: number): Array<string> =>
  row.length === width ? row : [...row, ...Array.from({ length: width - row.length }, () => '')];

// A real table splits most of its rows into cells. Prose with a stray comma, or a file with an
// unterminated quote, leaves a header row with whole lines sitting underneath it instead.
const hasTableShape = (body: Array<Array<string>>): boolean => {
  const rowsWithCells = body.filter((row) => row.length > 1).length;
  return rowsWithCells > 0 && rowsWithCells * 2 >= body.length;
};

/**
 * Returns `undefined` when the text has no table shape to show — the caller falls back to
 * rendering it as plain text.
 */
export const parseDelimitedText = (text: string): DelimitedTable | undefined => {
  if (text.length > MAXIMUM_PARSEABLE_LENGTH) {
    return undefined;
  }

  const delimiter = detectDelimiter(text);
  if (!delimiter) {
    return undefined;
  }

  const { data, errors } = parse<Array<string>>(text, { delimiter, skipEmptyLines: 'greedy' });

  // An unterminated quote still yields rows, with the rest of the file swallowed into one cell.
  // The raw text reads better than that.
  if (errors.some((error) => error.type === 'Quotes')) {
    return undefined;
  }

  const [headers, ...body] = data;
  if (!headers || headers.length < 2) {
    return undefined;
  }

  if (!hasTableShape(body)) {
    return undefined;
  }

  const width = body.reduce((widest, row) => Math.max(widest, row.length), headers.length);
  if ((body.length + 1) * width > MAXIMUM_RENDERED_CELLS) {
    return undefined;
  }

  return {
    headers: padToWidth(headers, width),
    rows: body.map((row) => padToWidth(row, width)),
    hasRowsWiderThanHeaders: width > headers.length,
  };
};
